// batchScan — recognise a dolly-label scan (Tier 2, spec'd 2026-07-30).
//
// BatchLabel prints a Code 128 of the batch number, shaped CB-YYYY-NNNNN
// (generate_cooling_batch_number: 'CB-' || year || '-' || ≥5-digit counter).
// That shape is the accept test everywhere a scan can land — a human can't
// produce it by accident, so the wedge listener never mistakes stray typing
// for a scan (useScanWedge contract) and the camera decoder ignores every
// other barcode in frame.

const BATCH_TAIL_RE = /CB-\d{4}-\d{5,}$/i

/** Wedge matcher: the keystroke buffer's TAIL is a batch number → canonical
 *  upper-case payload, else null. Junk before the scan is ignored. */
export function matchBatchNumber(buffer: string): string | null {
  const m = BATCH_TAIL_RE.exec(buffer.trim())
  return m ? m[0].toUpperCase() : null
}

/** Camera normaliser (createFrameDecoder): first decoded text that is a
 *  batch number wins; anything else in frame is ignored. */
export function batchFromTexts(texts: readonly string[]): string | null {
  for (const t of texts) {
    const m = matchBatchNumber(t)
    if (m) return m
  }
  return null
}

// Stored-bin label (F02 UF-05): generate_bin_number mints RBN-YYYY-NNNNN
// (5+ digit counter), and the bin label prints it as its Code 128. Same
// shape-only accept test as a batch number.
const BIN_TAIL_RE = /RBN-\d{4}-\d{5,}$/i

/** Wedge matcher: the keystroke buffer's TAIL is a stored-bin number →
 *  canonical upper-case payload, else null. */
export function matchBinNumber(buffer: string): string | null {
  const m = BIN_TAIL_RE.exec(buffer.trim())
  return m ? m[0].toUpperCase() : null
}

// Delivery sticker (spec #3956 §3–4): the label prints DLV- + the delivery's
// uuid without dashes (32 hex). No new numbering, and the shape can never be
// a batch or bin number. Canonical form: upper-case prefix, lower-case hex.
const DELIVERY_TAIL_RE = /DLV-([0-9a-f]{32})$/i

/** The code a delivery's sticker carries. */
export function deliveryLabelCode(deliveryId: string): string {
  return `DLV-${deliveryId.replace(/-/g, '').toLowerCase()}`
}

/** Wedge matcher: the keystroke buffer's TAIL is a delivery sticker →
 *  canonical payload, else null. */
export function matchDeliveryLabel(buffer: string): string | null {
  const m = DELIVERY_TAIL_RE.exec(buffer.trim())
  return m ? `DLV-${m[1].toLowerCase()}` : null
}

/** The delivery id (uuid, dashed) a canonical sticker code names. A DLP-
 *  code's tail is the PALLET's id, read the same way. */
export function deliveryIdFromLabel(code: string): string {
  const h = code.slice(4).toLowerCase()
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** Camera normaliser for the Tipping board: a pallet sticker, a delivery
 *  sticker or a dolly label, whichever is in frame first, as its canonical
 *  code. The board routes it by prefix (isDeliveryPalletLabel / isDeliveryLabel). */
export function tippingLabelFromTexts(texts: readonly string[]): string | null {
  for (const t of texts) {
    const m = matchDeliveryPalletLabel(t) ?? matchDeliveryLabel(t) ?? matchBatchNumber(t)
    if (m) return m
  }
  return null
}

/** A canonical code from the matchers above is a delivery sticker. */
export function isDeliveryLabel(code: string): boolean {
  return code.startsWith('DLV-')
}

// Pallet sticker (spec #4006 §4.3): DLP- + the delivery pallet's uuid without
// dashes. One per pallet of a delivery, so a scan names the PALLET, not just the
// delivery. Same canonical form as DLV-.
const PALLET_TAIL_RE = /DLP-([0-9a-f]{32})$/i

/** The code a delivery pallet's sticker carries. */
export function deliveryPalletLabelCode(palletId: string): string {
  return `DLP-${palletId.replace(/-/g, '').toLowerCase()}`
}

/** Wedge matcher: the keystroke buffer's TAIL is a pallet sticker → canonical
 *  payload, else null. */
export function matchDeliveryPalletLabel(buffer: string): string | null {
  const m = PALLET_TAIL_RE.exec(buffer.trim())
  return m ? `DLP-${m[1].toLowerCase()}` : null
}

/** A canonical code from the matchers above is a pallet sticker. */
export function isDeliveryPalletLabel(code: string): boolean {
  return code.startsWith('DLP-')
}

/** Wedge matcher for the Tipping board: a pallet sticker or a delivery sticker. */
export function matchIntakeSticker(buffer: string): string | null {
  return matchDeliveryPalletLabel(buffer) ?? matchDeliveryLabel(buffer)
}

/** Camera normaliser for QC → To check: a pallet or a delivery sticker. */
export function intakeStickerFromTexts(texts: readonly string[]): string | null {
  for (const t of texts) {
    const m = matchIntakeSticker(t)
    if (m) return m
  }
  return null
}
